import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * useModuleAccess — single source of truth for BRIDGE module gating.
 *
 * Standard-tier modules (phc-core, trial-site-core, gov-core, shared-services)
 * are enabled for every authenticated user by default.
 *
 * Escrow-tier modules (ebo-alerts, denguard, twinnet, avaref) require:
 *   - an active organization_module_entitlements row (status='active')
 *   - a countersigned licence (licence_accepted_at IS NOT NULL)
 *   - the user on the (non-revoked) organization_module_users allowlist
 *   - not expired
 *
 * FAILS CLOSED: any error → access denied. Escrow gating must never leak on error.
 */
export function useModuleAccess(moduleCode: string | null | undefined) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["module-access", user?.id, moduleCode],
    enabled: !!user?.id && !!moduleCode,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user?.id || !moduleCode) return false;
      const { data, error } = await supabase.rpc("has_module_access", {
        _user_id: user.id,
        _module_code: moduleCode,
      });
      if (error) {
        // fail closed
        console.warn("[useModuleAccess] RPC error → denying access", error);
        return false;
      }
      return Boolean(data);
    },
  });

  return {
    hasAccess: query.data === true,
    loading: query.isLoading,
    error: query.error,
  };
}
